import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../infrastructure/api/api.service';
import { Observable, concat, concatMap, of, throwError, toArray } from 'rxjs';
import {
  IAUMStreamPoint,
  IPeriodicStream,
  IPeriodicStreamPoint,
  PERIODIC_STREAMS,
  PERIODIC_STREAM_GROUPS,
  PERIODIC_STREAM_REQUEST_MODE,
  TPeriodicStreamRequestMode,
  TPeriodicStream,
  TPeriodicStreamRequest,
  TPeriodicStreamUpdateRequest,
  IPublicReturnStreamPoint,
} from '@aksia/models';
import {
  DataProviderEnum,
  EntityTypeEnum,
  StreamPeriodicityEnum,
} from '@aksia/enums';
import { IErrorResult, UTILS } from '@aksia/infrastructure';
import { PropExtend } from '@aksia/infrastructure';

@Injectable({
  providedIn: 'root',
})
export class PeriodicStreamService {
  private api = inject(ApiService);

  public updateStreams = (
    streamUpdateRequests: Array<TPeriodicStreamUpdateRequest>,
  ): Observable<Array<IPeriodicStreamPoint | IErrorResult>> => {
    const nonEndOfMonthAsOfs = this.getNonEndOfMonthAsOfs(streamUpdateRequests);
    if (nonEndOfMonthAsOfs.length > 0) {
      return throwError(
        () =>
          new Error(
            `Cannot update periodic streams. The following as of dates are not end of month: ${nonEndOfMonthAsOfs.join(', ')}`,
          ),
      );
    }
    const streamRequestURIs$: Array<
      Observable<IPeriodicStreamPoint | IErrorResult>
    > = [];
    streamUpdateRequests?.forEach((request: TPeriodicStreamUpdateRequest) => {
      request.datapoints = this.toDtoDataPoints(
        request.entityId,
        request.entityTypeId,
        request.streamType,
        request.datapoints as Array<
          IPeriodicStreamPoint | IAUMStreamPoint | IPublicReturnStreamPoint
        >,
      );
      streamRequestURIs$.push(
        request.httpMethod === 'PUT'
          ? this.api.put(request.uri!, request.datapoints)
          : this.api.post(request.uri!, request.datapoints),
      );
    });
    return concat(...streamRequestURIs$).pipe(
      toArray(),
      concatMap((results: Array<IPeriodicStreamPoint | IErrorResult>) => {
        return of(
          results?.flat() as Array<IPeriodicStreamPoint | IErrorResult>,
        );
      }),
    );
  };

  public getStreams = (
    streamsRequest: Array<TPeriodicStreamRequest>,
  ): Observable<Array<IPeriodicStream> | undefined> => {
    const streamRequestURIs$: Array<Observable<unknown>> = [];
    Array.from(new Set(streamsRequest?.map((request) => request.uri)))
      ?.filter(Boolean)
      ?.forEach((uri: string | undefined) => {
        streamRequestURIs$.push(this.api.get(uri!));
      });
    return concat(...streamRequestURIs$).pipe(
      toArray(),
      concatMap((results: Array<PropExtend<unknown>>) => {
        const streamsDTO: Array<IPeriodicStream> = [];
        streamsRequest?.forEach(
          (streamRequest: TPeriodicStreamRequest, index) => {
            const result =
              streamRequest.requestMode ===
              PERIODIC_STREAM_REQUEST_MODE.HISTORICAL
                ? (results?.at(index) as PropExtend<unknown>)
                : results?.find(
                    (stream) => stream?.[streamRequest.streamType!],
                  )?.[streamRequest.streamType!];

            streamsDTO.push({
              entityId: streamRequest.entityId,
              entityTypeId: streamRequest.entityTypeId,
              streamType: streamRequest.streamType!,
              periodicity: streamRequest.periodicity as
                StreamPeriodicityEnum | undefined,
              datapoints: this.toPeriodicDataPoints(
                result,
                streamRequest.streamType,
                streamRequest.requestMode,
                streamRequest.asOf,
              ),
              ytd: result?.ytd,
            });
          },
        );
        return of(streamsDTO);
      }),
    );
  };

  public getYTDCalculation(
    entityId: number,
    entityTypeId: EntityTypeEnum,
    periodicStreamType: TPeriodicStream,
    stream: Array<IPeriodicStreamPoint>,
  ) {
    let dto = stream;
    return this.api.post(
      `periodicdata/${periodicStreamType}/actual/${DataProviderEnum.Ostrako_O2}@${entityId}_${entityTypeId}/stream/calculateytd`,
      dto,
    );
  }

  private toPeriodicDataPoints(
    result: PropExtend<unknown>,
    streamType: TPeriodicStream,
    streamMode: TPeriodicStreamRequestMode,
    asOf?: string,
  ): Array<IPeriodicStreamPoint> {
    switch (streamMode) {
      case PERIODIC_STREAM_REQUEST_MODE.MINMAX: {
        let maxPoint = {
          asOf: PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(streamType)
            ? result?.asOfDate
            : streamType === PERIODIC_STREAMS.PUBLIC_RETURNS
              ? result?.max?.asOf
              : result?.max?.asOf,
          source:
            PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(streamType) ||
            streamType === PERIODIC_STREAMS.PUBLIC_RETURNS
              ? result?.source
              : result?.max?.source,
          value: PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(streamType)
            ? result?.amount
            : streamType === PERIODIC_STREAMS.PUBLIC_RETURNS
              ? result?.max?.value
              : result?.max?.value,
        };
        return [this.toPeriodicStreamPoint(maxPoint, streamType)];
      }
      case PERIODIC_STREAM_REQUEST_MODE.ASOF:
        let asOfPoint = {
          asOf: asOf ? new Date(asOf) : undefined,
          source:
            PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(streamType) ||
            streamType === PERIODIC_STREAMS.PUBLIC_RETURNS
              ? result?.source
              : result?.source,
          value: PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(streamType)
            ? result?.amount
            : streamType === PERIODIC_STREAMS.PUBLIC_RETURNS
              ? result?.value
              : result?.value,
        };
        return [this.toPeriodicStreamPoint(asOfPoint, streamType)];
      case PERIODIC_STREAM_REQUEST_MODE.HISTORICAL:
        let datapoints = PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(streamType)
          ? result?.aum
          : result?.datapoints;
        return datapoints
          ?.map((point: PropExtend<unknown>) =>
            this.toPeriodicStreamPoint(point, streamType),
          )
          ?.toSorted(
            (a: any, b: any) =>
              new Date(a.asOf).getTime() - new Date(b.asOf).getTime(),
          );
    }
    return [];
  }

  private toPeriodicStreamPoint<IPeriodicStreamPoint>(
    point: IPeriodicStreamPoint,
    periodicStreamType: TPeriodicStream,
  ): IPeriodicStreamPoint {
    if (PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(periodicStreamType)) {
      const { asOfDate, amount, ...restPoint } = point as IAUMStreamPoint;
      (point as IAUMStreamPoint) = {
        asOf: asOfDate,
        value: amount,
        ...restPoint,
      };
    }
    return point;
  }

  private getNonEndOfMonthAsOfs(
    streamUpdateRequests: Array<TPeriodicStreamUpdateRequest>,
  ): Array<string> {
    return (
      streamUpdateRequests?.flatMap(
        (request: TPeriodicStreamUpdateRequest) =>
          request.datapoints
            ?.filter(
              (point) =>
                !(point as PropExtend<IPeriodicStreamPoint>).$isDeleted &&
                !(point as IAUMStreamPoint).isDeleted &&
                !(point as IPublicReturnStreamPoint).markedForDeletion &&
                !UTILS.DATE.isEndOfMonth(UTILS.DATE.toDate(point.asOf)),
            )
            ?.map(
              (point) =>
                `${request.streamType} ${
                  UTILS.FORMAT.toDateFormat(point.asOf, 'mm/dd/yyyy', true) ??
                  'unknown'
                }`,
            ) ?? [],
      ) ?? []
    );
  }

  private toDtoDataPoints(
    entityId: number,
    entityTypeId: EntityTypeEnum,
    streamType: TPeriodicStream,
    datapoints: Array<
      IPeriodicStreamPoint | IAUMStreamPoint | IPublicReturnStreamPoint
    >,
  ): Array<IPeriodicStreamPoint> {
    if (PERIODIC_STREAM_GROUPS.AUM_EXPAND.includes(streamType)) {
      return datapoints?.map((point: IAUMStreamPoint) => {
        let { asOf, value, ...restPoint } = point;
        return {
          asOfDate: asOf,
          amount: value,
          effectiveAsOfDate: new Date().toISOString(),
          fundId: entityId,
          entityId: entityId,
          entityTypeId: entityTypeId,
          ...restPoint,
        };
      });
    } else if (streamType === PERIODIC_STREAMS.PUBLIC_RETURNS) {
      return datapoints?.map((point: IPublicReturnStreamPoint) => {
        return {
          fundId: entityId,
          ...point,
        };
      });
    }
    return datapoints;
  }
}
